import { useMemo } from 'react'

import { useCampaign } from '../../../../hooks/Outreach/useCampaign'
import { Campaign, CampaignSystemKind } from '../../../../models/Outreach/types'
import { findPrimaryCampaign } from '../../utils/campaignDisplayStatus'
import { useLeasingSeasonDetail } from '../../hooks/useLeasingSeasonQueries'

export {
    useSeasonDetail,
    useSeasonIdFromQuery,
} from '../../hooks/useLeasingSeasonQueries'

export function useSeasonCampaigns(
    seasonId: number | undefined,
    primaryCampaignsFallback?: Campaign[],
) {
    const seasonQuery = useLeasingSeasonDetail(seasonId)
    const { campaigns, isLoadingCampaigns } = useCampaign()

    const season = seasonQuery.data

    const primaryCampaigns = useMemo(() => {
        const fromDetail = season?.primary_campaigns ?? []
        if (fromDetail.length > 0) return fromDetail
        return primaryCampaignsFallback ?? []
    }, [season?.primary_campaigns, primaryCampaignsFallback])

    const primaryEmail = useMemo(
        () =>
            findPrimaryCampaign(
                primaryCampaigns,
                CampaignSystemKind.PRIMARY_EMAIL,
            ),
        [primaryCampaigns],
    )

    const primaryText = useMemo(
        () =>
            findPrimaryCampaign(
                primaryCampaigns,
                CampaignSystemKind.PRIMARY_TEXT,
            ),
        [primaryCampaigns],
    )

    const customCampaigns = useMemo(() => {
        if (!seasonId || !campaigns) return []
        return campaigns.filter(
            (c: Campaign) => c.leasing_season === seasonId && !c.system_kind,
        )
    }, [campaigns, seasonId])

    const allSeasonCampaigns = useMemo(() => {
        return [...primaryCampaigns, ...customCampaigns]
    }, [primaryCampaigns, customCampaigns])

    return {
        season,
        seasonQuery,
        primaryEmail,
        primaryText,
        customCampaigns,
        allSeasonCampaigns,
        refetchSeason: seasonQuery.refetch,
        isLoading:
            (seasonId != null && seasonQuery.isLoading) || isLoadingCampaigns,
    }
}
